import { reactive } from 'vue'
import { deepCopy } from '@/util/deep-copy'
import { hideReservationMenu } from './sheet-overlays'

/*
 * 예약 선택과 시간선택 모드 — 구 V2ReservationManagementDataController 에서 떼어냈다
 * (MDP-2295 ④-5 4단계).
 *
 * 두 가지를 담는다.
 *
 * 1) 지금 고른 것 — 예약 하나, 그 고객, 그 예약을 그린 DOM 요소.
 *    예약을 바꿔 고르면 열려 있던 예약 메뉴는 닫는다(선택이 바뀌었는데 이전 예약의
 *    메뉴가 떠 있으면 엉뚱한 대상에 명령이 나간다).
 *
 * 2) 시간선택 모드 — 예약을 복사·잘라내기 한 뒤 "어디에 놓을지" 를 시트에서 고르는 상태다.
 *    - copy    : 내용만 복제한다. 상태(statusID)도 함께 가져간다
 *    - cut     : 예약을 통째로 들고 간다
 *    - fromSaaS: 다른 창(SaaS)에서 고객을 들고 넘어온 경우
 *    - none    : 모드 아님
 *
 * 메뉴를 닫는 일은 5단계에서 함께 옮겨져 sheet-overlays 가 맡는다.
 */

export const TimeSelectModeType = {
    none: 'none',
    copy: 'copy',
    cut: 'cut',
    fromSaaS: 'fromSaaS',
} as const

export type TimeSelectMode = (typeof TimeSelectModeType)[keyof typeof TimeSelectModeType]

type ReservationLike = Record<string, unknown>

export const reservationSelection = reactive({
    /** 지금 고른 예약 */
    selectedReservation: undefined as ReservationLike | undefined,
    /** 지금 고른 고객 */
    selectedHService: undefined as unknown,
    /** 그 예약을 그린 DOM 요소 — 팝업 위치를 잡는 데 쓴다 */
    selectedReservationElement: undefined as unknown,

    isTimeSelectMode: false,
    isShowTimeSelectInfo: false,
    timeSelectModeType: undefined as TimeSelectMode | undefined,
})

/** 예약을 골라 둔다 — 고르는 순간 이전 예약의 메뉴는 닫는다 */
export function setSelectedReservation(newValue: ReservationLike | undefined): void {
    hideReservationMenu()
    reservationSelection.selectedReservation = newValue
}

/**
 * 시간선택 모드로 들어간다.
 *
 * cut 은 예약을 통째로 들고 가고, 나머지는 필요한 값만 새 예약으로 추린다.
 * copy 일 때만 상태(statusID)를 함께 가져간다 — 잘라내기는 원본이 사라지므로
 * 상태를 그대로 옮기면 되지만, 복사는 새 예약이라 상태를 이어받는 것이 의도된 동작이다.
 */
export function setTimeSelectMode(
    hservice: unknown,
    reservation: ReservationLike,
    modeType: TimeSelectMode = TimeSelectModeType.none,
): void {
    reservationSelection.timeSelectModeType = modeType

    const { programID, duration, note, statusID } = reservation
    let r: ReservationLike | undefined

    switch (modeType) {
        case TimeSelectModeType.cut:
            r = deepCopy(reservation)
            break
        default:
            r = {
                hserviceID: reservationSelection.selectedHService,
                programID,
                duration,
                note,
            }
            if (modeType == TimeSelectModeType.copy) {
                r.statusID = statusID
            }
            break
    }

    reservationSelection.selectedHService = hservice
    setSelectedReservation(r)
    reservationSelection.isTimeSelectMode = true
}

/** SaaS 에서 넘어온 모드 표시만 지운다 (선택 자체는 그대로 둔다) */
export function clearSaaSTimeSelectMode(): void {
    reservationSelection.timeSelectModeType = TimeSelectModeType.none
}

export function exitTimeSelectMode(): void {
    reservationSelection.isTimeSelectMode = false
}
